/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 *
 * Creatable multi-select (used for SKU tags). Same pick-or-type-to-create
 * behavior as CreatableSelect, but holds several values shown as removable
 * chips. `options` are pre-entered values to pick from; typing a new one + Enter
 * (or the "+ Create" row) adds it. `value`/`onChange` are a string[].
 */

import { useRef, useState } from "react";
import { useClickOutside } from "../hooks/useClickOutside.js";

export default function CreatableMultiSelect({ options = [], value = [], onChange, placeholder = "Select or type…" }) {
  const [inputVal, setInputVal] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false), open);

  const selected = value || [];
  const trimmed = inputVal.trim();
  const has = (t) => selected.some((s) => s.toLowerCase() === t.toLowerCase());
  const avail = options.filter((o) => !has(o) && o.toLowerCase().includes(trimmed.toLowerCase()));
  const canCreate = trimmed.length > 0 && !has(trimmed) && !options.some((o) => o.toLowerCase() === trimmed.toLowerCase());

  function add(tag) {
    const t = tag.trim();
    if (t && !has(t)) onChange([...selected, t]);
    setInputVal("");
  }
  function remove(tag) { onChange(selected.filter((s) => s !== tag)); }

  function onKeyDown(e) {
    if (e.key === "Enter" && trimmed) { e.preventDefault(); add(trimmed); }
    else if (e.key === "Backspace" && !inputVal && selected.length) remove(selected[selected.length - 1]);
    else if (e.key === "Escape") setOpen(false);
  }

  return (
    <div ref={ref} className="cs-wrap">
      <div className="cs-control cs-multi" onClick={() => setOpen(true)}>
        {selected.map((t) => (
          <span key={t} className="cs-chip">
            {t}
            <button type="button" className="cs-chip-x" onMouseDown={(e) => { e.stopPropagation(); remove(t); }} aria-label={`Remove ${t}`}>×</button>
          </span>
        ))}
        <input
          className="cs-input"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={selected.length ? "" : placeholder}
        />
        <svg className="cs-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {open && (
        <div className="cs-menu">
          {avail.length === 0 && !canCreate && <div className="cs-empty">Type to add a tag…</div>}
          {avail.map((o) => (
            <div key={o} className="cs-opt" onMouseDown={() => add(o)}>{o}</div>
          ))}
          {canCreate && (
            <div className="cs-opt cs-create" onMouseDown={() => add(trimmed)}>{`+ Create “${trimmed}”`}</div>
          )}
        </div>
      )}
    </div>
  );
}
